import React, { useCallback, useEffect, useState } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import Table from '../components/Table';
import './Bookings.css';

function safeDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function getMonthKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function formatMonthLabel(monthKey) {
  const [y, m] = monthKey.split('-').map((v) => Number(v));
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
}

function toNumberOrZero(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('all'); // 01-12 or 'all'
  const [expandedMonths, setExpandedMonths] = useState(() => new Set());

  const filters = [
    { key: 'all', label: 'All Booking' },
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'completed', label: 'Completed' },
    { key: 'cancelled', label: 'Cancelled' },
    { key: 'walkin', label: 'Walk-in' }
  ];

  const columns = [
    {
      key: 'bookingReference',
      title: 'Booking ID',
      className: 'booking-id-col',
      render: (value) => <div>{value}</div>
    },
    {
      key: 'customerName',
      title: 'Customer Name',
      render: (value, row) => row.customerNameWithCount ?? value
    },
    {
      key: 'customerPhone',
      title: 'Customer Number'
    },
    {
      key: 'salonName',
      title: 'Salon Name'
    },
    {
      key: 'salonId',
      title: 'Salon ID',
      className: 'salon-id-col'
    },
    {
      key: 'serviceName',
      title: 'Service Type'
    },
    {
      key: 'bookingDateTime',
      title: 'Date/Time',
      render: (value, row) => {
        const date = new Date(row.bookingDate);
        return `${date.toLocaleDateString()} ${row.bookingTime}`;
      }
    },
    {
      key: 'bookingStatus',
      title: 'Status',
      render: (value) => (
        <span className={`status-badge status-${value.toLowerCase()}`}>
          {value}
        </span>
      )
    },
    {
      key: 'offerAppliedBool',
      title: 'Offer Applied',
      render: (value) => (value ? 'Yes' : 'No')
    },
    {
      key: 'offerPercent',
      title: 'Offer %',
      render: (value) => (value != null ? `${value}%` : '—')
    },
    {
      key: 'totalPrice',
      title: 'Final Price',
      render: (value) => `₹${toNumberOrZero(value)}`
    },
    {
      key: 'advancePaid',
      title: 'Advance',
      // Advance Pay (₹11 Razorpay advance) — settlement lives in /settlements
      render: (value, row) =>
        row.advancePaid === true ? (
          <span className="status-badge status-confirmed" title={`₹${Math.max(Number(row.remainingAmount) || 0, 0)} payable at salon`}>
            ₹{row.advanceAmount || 11} paid
          </span>
        ) : (
          '—'
        )
    }
  ];

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const bookingsQuery = query(
        collection(db, 'bookings'),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(bookingsQuery);
      const bookingsData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();

        // Advance Pay payment holds (payment_* statuses) are transient Cloud-Function
        // states, not real bookings — money oversight lives in /settlements.
        if (String(data.bookingStatus || '').startsWith('payment_')) return;

        bookingsData.push({
          id: doc.id,
          bookingId: doc.id,
          bookingReference: data.bookingReference,
          customerName: data.customerName,
          customerId: data.customerId,
          customerPhone: data.customerPhone,
          salonName: data.salonName,
          serviceName: data.serviceName,
          bookingDate: data.bookingDate,
          bookingTime: data.bookingTime,
          bookingStatus: data.bookingStatus,
          originalPrice: data.originalPrice ?? data.servicePrice ?? data.totalPrice ?? 0,
          totalPrice: data.totalPrice ?? 0,
          paymentMode: data.paymentMode,
          createdAt: data.createdAt,
          salonId: data.salonId,
          isWalkIn: data.isWalkIn || false,
          offerApplied: data.offerApplied ?? false,
          offerType: data.offerType ?? 'none',
          advancePaid: data.advancePaid === true,
          advanceAmount: data.advanceAmount ?? null,
          remainingAmount: data.remainingAmount ?? null
        });
      });
      
      // Compute offer applied + percentage (avoid relying only on offerType).
      for (const b of bookingsData) {
        const original = toNumberOrZero(b.originalPrice);
        const total = toNumberOrZero(b.totalPrice);
        const discount = Math.max(0, original - total);
        const hasOffer = discount > 0 || (b.offerApplied != null && b.offerApplied !== false);
        b.offerAppliedBool = hasOffer;
        b.offerPercent =
          hasOffer && original > 0
            ? Math.round((discount / original) * 100)
            : null;
      }

      // Compute each customer's Nth booking (chronological).
      const byCustomerKey = new Map();
      const sortedAsc = [...bookingsData].sort((a, b) => {
        const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (safeDate(a.bookingDate)?.getTime() ?? 0);
        const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (safeDate(b.bookingDate)?.getTime() ?? 0);
        return timeA - timeB;
      });

      for (const b of sortedAsc) {
        const key = b.customerId || b.customerPhone || (b.customerName ? String(b.customerName).toLowerCase() : '');
        if (!key) continue;
        byCustomerKey.set(key, (byCustomerKey.get(key) ?? 0) + 1);
        b.customerBookingNumber = byCustomerKey.get(key);
      }

      const withCounts = bookingsData.map((b) => {
        const n = b.customerBookingNumber;
        const name = b.customerName ?? '';
        return {
          ...b,
          customerNameWithCount: name && n ? `${name}(${n})` : name
        };
      });

      // Default selectors: latest year, all months.
      const years = Array.from(
        new Set(
          withCounts
            .map((b) => safeDate(b.bookingDate))
            .filter(Boolean)
            .map((d) => d.getFullYear())
        )
      ).sort((a, b) => b - a);

      if (years.length > 0) {
        setSelectedYear((prev) => prev || String(years[0]));
      }

      setBookings(withCounts);
    } catch (err) {
      setError('Failed to load bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const applyFilter = useCallback(() => {
    let filtered = [...bookings];

    // "All / Upcoming / Completed / Cancelled" are for online bookings only.
    // Walk-ins should appear only under the dedicated Walk-in tab.
    if (activeFilter !== 'walkin') {
      filtered = filtered.filter((b) => b.isWalkIn !== true);
    }
    
    switch (activeFilter) {
      case 'upcoming':
        filtered = filtered.filter(booking => {
          const status = booking.bookingStatus.toLowerCase();
          return status === 'upcoming' || status === 'confirmed' || status === 'booked';
        });
        break;
      case 'completed':
        filtered = filtered.filter(booking => booking.bookingStatus.toLowerCase() === 'completed');
        break;
      case 'cancelled':
        filtered = filtered.filter(booking => {
          const status = booking.bookingStatus.toLowerCase();
          return status === 'cancelled_by_salon' || status === 'cancelled_by_customer' || status === 'cancelled';
        });
        break;
      case 'walkin':
        filtered = filtered.filter((booking) => booking.isWalkIn === true);
        break;
      default:
        break;
    }

    // Apply year/month selectors (based on bookingDate).
    const yearNum = selectedYear ? Number(selectedYear) : NaN;
    if (Number.isFinite(yearNum)) {
      filtered = filtered.filter((b) => {
        const d = safeDate(b.bookingDate);
        return d ? d.getFullYear() === yearNum : false;
      });
    }
    if (selectedMonth !== 'all') {
      filtered = filtered.filter((b) => {
        const d = safeDate(b.bookingDate);
        if (!d) return false;
        const m = String(d.getMonth() + 1).padStart(2, '0');
        return m === selectedMonth;
      });
    }
    
    // Sort by createdAt descending (most recent first)
    filtered.sort((a, b) => {
      const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
      const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
      return timeB - timeA;
    });
    
    setFilteredBookings(filtered);
  }, [activeFilter, bookings, selectedMonth, selectedYear]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  useEffect(() => {
    applyFilter();
  }, [applyFilter]);

  const handleRefresh = () => {
    fetchBookings();
  };

  const yearsForSelect = Array.from(
    new Set(
      bookings
        .map((b) => safeDate(b.bookingDate))
        .filter(Boolean)
        .map((d) => d.getFullYear())
    )
  ).sort((a, b) => b - a);

  const monthOptions = [
    { key: 'all', label: 'All months' },
    { key: '01', label: 'January' },
    { key: '02', label: 'February' },
    { key: '03', label: 'March' },
    { key: '04', label: 'April' },
    { key: '05', label: 'May' },
    { key: '06', label: 'June' },
    { key: '07', label: 'July' },
    { key: '08', label: 'August' },
    { key: '09', label: 'September' },
    { key: '10', label: 'October' },
    { key: '11', label: 'November' },
    { key: '12', label: 'December' }
  ];

  const groupedByMonth = filteredBookings.reduce((acc, b) => {
    const d = safeDate(b.bookingDate);
    if (!d) return acc;
    const key = getMonthKey(d);
    if (!acc[key]) acc[key] = [];
    acc[key].push(b);
    return acc;
  }, {});

  const monthKeys = Object.keys(groupedByMonth).sort((a, b) => b.localeCompare(a));

  const toggleMonthExpanded = (monthKey) => {
    setExpandedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(monthKey)) next.delete(monthKey);
      else next.add(monthKey);
      return next;
    });
  };

  useEffect(() => {
    // When month selector is specific, expand that month (and collapse others for clarity).
    if (selectedMonth === 'all' || !selectedYear) return;
    const mk = `${selectedYear}-${selectedMonth}`;
    setExpandedMonths(new Set([mk]));
  }, [selectedYear, selectedMonth]);

  if (error) {
    return (
      <div className="bookings-page">
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button onClick={handleRefresh} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bookings-page">
      <div className="page-header">
        <div className="header-info">
          <h2>Bookings Management</h2>
          <p>Total: {filteredBookings.length} bookings</p>
        </div>
        <button onClick={handleRefresh} className="refresh-btn" disabled={loading}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      <div className="filters-container">
        <div className="filters">
          {filters.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`filter-btn ${activeFilter === filter.key ? 'active' : ''}`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="month-filter-bar">
        <div className="month-filter-left">
          <label className="month-filter-field">
            <span className="month-filter-label">Year</span>
            <select
              className="month-filter-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              disabled={loading || yearsForSelect.length === 0}
            >
              {yearsForSelect.map((y) => (
                <option key={y} value={String(y)}>
                  {y}
                </option>
              ))}
            </select>
          </label>

          <label className="month-filter-field">
            <span className="month-filter-label">Month</span>
            <select
              className="month-filter-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              disabled={loading}
            >
              {monthOptions.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="month-filter-right">
          <button
            className="month-filter-reset-btn"
            onClick={() => {
              setSelectedMonth('all');
              setExpandedMonths(new Set());
            }}
            disabled={loading}
          >
            Reset
          </button>
        </div>
      </div>

      <div className="table-section">
        {monthKeys.length === 0 ? (
          <Table
            columns={columns}
            data={[]}
            loading={loading}
            emptyMessage="No bookings found for the selected filters"
          />
        ) : (
          <div className="month-groups">
            {monthKeys.map((monthKey) => {
              const monthBookings = groupedByMonth[monthKey];
              const isExpanded = expandedMonths.has(monthKey) || monthKeys.length === 1;
              return (
                <div key={monthKey} className="month-group">
                  <button
                    className={`month-group-header ${isExpanded ? 'expanded' : ''}`}
                    onClick={() => toggleMonthExpanded(monthKey)}
                    type="button"
                  >
                    <div className="month-group-title">
                      <span className="month-group-chevron">{isExpanded ? '▾' : '▸'}</span>
                      <span className="month-group-label">{formatMonthLabel(monthKey)}</span>
                    </div>
                    <div className="month-group-meta">
                      <span className="month-group-count">{monthBookings.length} bookings</span>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="month-group-body">
                      <Table
                        columns={columns}
                        data={monthBookings}
                        loading={loading}
                        emptyMessage="No bookings found for this month"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Bookings;



